import { ReactNode, useContext, useState } from 'react';
import { Box, Flex, Stack, VStack, Text, Link, useDisclosure } from '@chakra-ui/react';

import { AuthContext, MeContext } from 'auth/AuthContextProvider';
import { Button } from 'components/Button';
import { ConfirmationModalWithInput } from 'components/ConfirmationModalWithInput';
import { ResultModal } from 'components/ResultModal';
import { EnvelopeIcon } from 'components/Icons';
import { Projet } from 'types';

export type Props = {
  file: Projet;
  children: ReactNode;
};

/**
 * Affiche la liste des étapes (children). Tant que le dossier n'a pas été transféré
 * à un courtier (est_atraiter === false), l'avancement est flouté et un encart CTA
 * invite à transférer le dossier pour suivre l'avancement du financement.
 */
export const EtapesAvancementOverlay = ({ file, children }: Props) => {
  const { token } = useContext(AuthContext);
  const me = useContext(MeContext);
  const isSimply = !!me.utilisateur?.organisation?.mode_simply;

  const stateTransfertAction = useDisclosure();
  const stateResultAction = useDisclosure();

  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    extra?: ReactNode;
  } | null>(null);

  // Dossier déjà pris en charge par un courtier : on affiche l'avancement réel, sans floutage.
  if (file.est_atraiter !== false) {
    return <>{children}</>;
  }

  const idDossier = file.id as number;
  const type = file.type as string;
  const etoiles = file.eval;
  // Un dossier au scoring <= 2 étoiles est considéré comme non finançable.
  const nonFinancable = etoiles !== undefined && etoiles <= 2;

  const texte_avertissement =
    file.apport !== undefined &&
    file.capacite_emprunt_max !== undefined &&
    file.montant !== undefined &&
    file.apport + file.capacite_emprunt_max < file.montant
      ? 'Attention le projet présente une anomalie,\\nil manque un financement de ' +
        `${Math.round(file.montant - file.apport - file.capacite_emprunt_max).toLocaleString(
          'fr-FR',
        )} €` +
        ' pour ce projet'
      : '';

  // Écran de confirmation affiché aux utilisateurs classiques (non "simply") après le transfert.
  const transfertSuccessContentClassique = (
    <Text color="gray.600">
      Votre acquéreur sera contacté sous 48h00 maximum par un courtier expert.
    </Text>
  );

  // Écran de confirmation affiché aux utilisateurs "simply" après le transfert.
  const transfertSuccessContent = (
    <VStack spacing={4}>
      {nonFinancable && (
        <Box bg="red.50" borderRadius="lg" px={5} py={3}>
          <Text color="red.500" fontWeight="semibold" fontSize="sm">
            Ce dossier est non finançable (scoré {etoiles} étoile
            {(etoiles as number) > 1 ? 's' : ''}), il nécessite une analyse plus approfondie par
            notre équipe.
            <br />
            Un expert courtier contactera votre client dès sa prise en charge.
          </Text>
        </Box>
      )}
      {!nonFinancable && (
        <Text color="gray.600">
          Votre acquéreur sera contacté sous 48h00 maximum par un courtier expert.
        </Text>
      )}
      <Text color="gray.600">
        Nous vous invitons à passer au pack supérieur afin d&apos;être éligible à une commission.
      </Text>
      <Box>
        <Text color="gray.600">Contactez-nous par mail :</Text>
        <Stack direction="row" align="center" justify="center" spacing={2}>
          <EnvelopeIcon boxSize="18px" color="teal.500" />
          <Link href="mailto:direction@monemprunt.com" color="teal.500" fontWeight="semibold">
            direction@monemprunt.com
          </Link>
        </Stack>
      </Box>
    </VStack>
  );

  const transfert_dossier: (value: number) => void = (value) => {
    fetch('/API/dossiers/process/' + idDossier, {
      method: 'POST',
      body: JSON.stringify({ montant_projet: value }),
      headers: new Headers({
        Authorization: token,
        'Content-Type': 'application/json',
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        setResult({
          success: !!json.success,
          message:
            json.success && isSimply
              ? 'Le dossier a été transféré à un courtier Monemprunt.com pour traitement.'
              : json.message ?? '',
          extra: json.success
            ? isSimply
              ? transfertSuccessContent
              : transfertSuccessContentClassique
            : undefined,
        });
        stateResultAction.onOpen();
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const handleResultClose = () => {
    stateResultAction.onClose();
    setResult(null);
  };

  return (
    <Box position="relative" w="100%">
      {/* Étapes floutées : visibles mais non interactives tant que le dossier n'est pas transféré. */}
      <Box filter="blur(5px)" pointerEvents="none" userSelect="none" aria-hidden="true">
        {children}
      </Box>

      {/* Encart CTA au-dessus du floutage, contenu dans les limites de l'overlay. */}
      <Flex position="absolute" inset={0} align="center" justify="center" p={2} overflow="hidden">
        <Flex
          align="center"
          gap={4}
          maxW="640px"
          w="100%"
          bg="white"
          borderRadius="12px"
          boxShadow="md"
          px={5}
          py={3}
        >
          <Text fontWeight="semibold" fontSize="sm" color="teal.900" flex={1}>
            Transférez ce dossier à un courtier pour suivre l&apos;avancement du financement
          </Text>
          <Button
            onClick={stateTransfertAction.onOpen}
            minH={9}
            px={6}
            fontSize="sm"
            flexShrink={0}
          >
            Transférer à un courtier
          </Button>
        </Flex>
      </Flex>

      <ConfirmationModalWithInput
        titre="Transfert du dossier au courtier"
        description="Indiquez le montant total du projet pour transférer ce dossier à un courtier Monemprunt.com."
        avertissement={texte_avertissement}
        isOpen={stateTransfertAction.isOpen}
        onClose={stateTransfertAction.onClose}
        textButtonValider={'Transférer'}
        minimumValue={50000}
        colorButtonValider={'teal'}
        onValidate={transfert_dossier}
        idDossier={idDossier}
        type={type}
      />
      <ResultModal
        isOpen={stateResultAction.isOpen}
        onClose={handleResultClose}
        success={result?.success ?? false}
        message={result?.message ?? ''}
        extraContent={result?.extra}
      />
    </Box>
  );
};
